/**
 * Random map generation, ported from legacy/admirals-return.html genMap().
 * Improvements over legacy (logged in TASKS.md session log):
 *  - RNG is injected (seeded, in G) instead of a Date-seeded global.
 *  - Explicit ocean-connectivity guarantee: all water is one flood-fillable sea,
 *    so every city is reachable. (Legacy only forced water edge columns.)
 */
import { hexDist, key, inBounds, neighbors } from './board';
import type { Rng } from './rng';
import type { City, GameState, Side } from './state';

export const CITY_NAMES: readonly string[] = [
  'Norfolk', 'San Diego', 'Boston', 'Charleston', 'Savannah', 'Galveston', 'Pensacola',
  'New Orleans', 'Seattle', 'Baltimore', 'Key West', 'Mobile', 'Corpus Christi', 'Newport',
  'Astoria', 'Monterey', 'Tampa', 'Wilmington', 'Portland', 'New Haven', 'Annapolis',
  'Bar Harbor', 'St. Louis', 'Akron',
];

const CITY_VP_TABLE = [200, 200, 200, 300, 300, 400, 500] as const;
const MIN_CITY_SPACING = 3;
const HOME_CITIES_PER_SIDE = 2;

function shuffle<T>(arr: T[], rng: Rng): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    const a = arr[i] as T;
    arr[i] = arr[j] as T;
    arr[j] = a;
  }
}

/** All water connected? (flood fill from any water tile) */
function oceanConnected(g: GameState): boolean {
  let start: [number, number] | null = null;
  let waterCount = 0;
  for (let r = 0; r < g.H; r++) {
    for (let c = 0; c < g.W; c++) {
      if (g.tiles[r]![c] === 'water') {
        waterCount++;
        start ??= [c, r];
      }
    }
  }
  if (!start) return false;
  const seen = new Set<string>([key(...start)]);
  const q: [number, number][] = [start];
  while (q.length) {
    const [c, r] = q.pop()!;
    for (const [nc, nr] of neighbors(g, c, r)) {
      const k = key(nc, nr);
      if (g.tiles[nr]![nc] === 'water' && !seen.has(k)) {
        seen.add(k);
        q.push([nc, nr]);
      }
    }
  }
  return seen.size === waterCount;
}

export function genMap(g: GameState, rng: Rng, depth = 0): void {
  if (depth > 40) throw new Error(`mapgen failed to converge (seed ${g.seed})`);
  g.cities = [];
  for (let r = 0; r < g.H; r++) {
    g.tiles[r] = [];
    for (let c = 0; c < g.W; c++) g.tiles[r]!.push('water');
  }

  // Stratified land placement: one random-walk blob per map cell so land spreads
  // across the whole ocean. The grid scales up on the large map — it has far more
  // area than medium for the same 4×2 grid, which left too little COASTLINE to fit
  // its cities (now coastal LAND tiles) at MIN_CITY_SPACING; an extra row+column
  // of blobs lengthens the shoreline so mapgen converges.
  const cols = g.W >= 24 ? 5 : 4;
  const rows = g.H >= 16 ? 3 : 2;
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      if (rng.next() < 0.18) continue; // occasional open-sea cell
      const cw = (g.W - 4) / cols;
      const ch = (g.H - 2) / rows;
      let c = Math.floor(2 + cx * cw + rng.next() * cw);
      let r = Math.floor(1 + cy * ch + rng.next() * ch);
      const size = 4 + rng.int(6);
      for (let i = 0; i < size; i++) {
        if (inBounds(g, c, r)) g.tiles[r]![c] = 'land';
        const ns = neighbors(g, c, r);
        if (ns.length === 0) break;
        const [nc, nr] = rng.pick(ns);
        c = nc;
        r = nr;
      }
    }
  }
  // Edge columns always water (fleet deployment lanes).
  for (let r = 0; r < g.H; r++) {
    g.tiles[r]![0] = 'water';
    g.tiles[r]![g.W - 1] = 'water';
  }

  // Cities: LAND tiles adjacent to open water ("coastal towns"). A city tile is
  // impassable land — ships can't sail onto it; you capture/hold it from an
  // ADJACENT water tile (see turn.ts endOfTurn). So a city must have ≥1 water
  // neighbour, or no transport could ever reach it.
  const cand: [number, number][] = [];
  for (let r = 0; r < g.H; r++) {
    for (let c = 0; c < g.W; c++) {
      if (g.tiles[r]![c] !== 'land') continue;
      if (neighbors(g, c, r).some(([nc, nr]) => g.tiles[nr]![nc] === 'water')) cand.push([c, r]);
    }
  }
  shuffle(cand, rng);
  const names = [...CITY_NAMES];
  shuffle(names, rng);
  for (const [c, r] of cand) {
    if (g.cities.length >= g.nCities) break;
    if (g.cities.some((ct) => hexDist(ct.c, ct.r, c, r) < MIN_CITY_SPACING)) continue;
    const vp = rng.pick(CITY_VP_TABLE);
    const city: City = {
      c, r,
      name: names.pop() ?? `Port ${g.cities.length + 1}`,
      vp, owner: null, home: null,
    };
    g.cities.push(city);
  }

  // Retry whole map if too few cities landed or the ocean is split.
  if (g.cities.length < Math.max(6, g.nCities - 4) || !oceanConnected(g)) {
    genMap(g, rng, depth + 1);
    return;
  }

  // Home cities: westernmost for Red, easternmost for Yellow.
  const sorted = [...g.cities].sort((a, b) => a.c - b.c);
  for (let i = 0; i < HOME_CITIES_PER_SIDE; i++) {
    const red = sorted[i]!;
    red.home = 'R';
    red.owner = 'R';
    const yellow = sorted[sorted.length - 1 - i]!;
    yellow.home = 'Y';
    yellow.owner = 'Y';
  }

  // Favored side = richer home VP; win ratio 15–45% in steps of 5. Ties are
  // common (city VPs are a small integer set), so break them with the seeded RNG
  // rather than always favouring Red — the favoured side must clear the ratio, so
  // a systematic Red bias would hand Yellow a systematic edge (it wins the ties).
  const homeVp = (s: Side): number =>
    g.cities.filter((x) => x.home === s).reduce((t, x) => t + x.vp, 0);
  const dv = homeVp('R') - homeVp('Y');
  g.favored = dv > 0 ? 'R' : dv < 0 ? 'Y' : (rng.next() < 0.5 ? 'R' : 'Y');
  g.ratio = 15 + rng.int(7) * 5;
}
